<template>
    <div class="china-device-map">
        <div ref="cityChart" class="chart-container"></div>
        <DashboardLoading
            :visible="initLoading && loading"
            text="加载全国设备分布数据"
            size="normal"
        />
    </div>
</template>

<script>
import * as echarts from 'echarts'
import { getProvinceDeviceStats, getCurrentLocation } from '@/api/dashboard'
import posIcon from '@/assets/images/dashboard/pos.png'
import DashboardLoading from './DashboardLoading.vue'
export default {
  name: 'ChinaDeviceMap',
  components: {
    DashboardLoading
  },
  props: {
    fontSizes: {
      type: Object,
      default: () => ({
        titleFontSize: 16,
        legendFontSize: 12,
        axisLabelFontSize: 10,
        axisNameFontSize: 12,
        tooltipFontSize: 14,
        dataLabelFontSize: 11,
        smallFontSize: 8,
        largeFontSize: 18
      })
    }
  },
  data () {
    return {
      chartInstance: null,
      refreshTimer: null,
      cityData: [],
      loading: false,
      currentLocation: null, // 当前定位信息
      locationLoading: false, // 定位加载状态
      locationAnimationTimer: null, // 定位图标动画定时器
      initLoading: true
    }
  },
  mounted () {
    this.fetchProvinceDeviceStats()
    this.fetchCurrentLocation()
    this.refreshTimer = setInterval(() => {
      console.log('重新初始化地图数据', this.$moment(new Date(), 'YYYY-MM-DD HH:mm:ss'))
      this.destroyChart()
      this.fetchProvinceDeviceStats()
    }, 600000)
  },
  beforeDestroy () {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
    if (this.locationAnimationTimer) {
      clearInterval(this.locationAnimationTimer)
    }
    this.destroyChart()
  },
  watch: {
    fontSizes: {
      handler () {
        this.updateChartOption()
      },
      deep: true
    }
  },
  methods: {
    async fetchProvinceDeviceStats () {
      try {
        this.loading = true
        const response = await getProvinceDeviceStats()
        console.log('response', response)
        if (response.data.status && response.data.data && response.data.data.provinceStats) {
          // 将API数据转换为组件需要的格式
          this.cityData = response.data.data.provinceStats.map(item => ({
            name: item.province,
            value: item.counts,
            devices: item.counts
          }))

          // 数据获取成功后初始化图表
          this.initChart()
        } else {
          console.error('获取省份设备统计数据失败:', response)
          // 如果API失败，使用空数据初始化图表
          this.cityData = []
          this.initChart()
        }
      } catch (error) {
        console.error('获取省份设备统计数据出错:', error)
        // 如果出错，使用空数据初始化图表
        this.cityData = []
        this.initChart()
      } finally {
        this.loading = false
        this.initLoading = false
      }
    },

    async fetchCurrentLocation () {
      try {
        this.locationLoading = true

        // 检查缓存
        const cacheKey = 'currentLocationData'
        const cachedData = this.getLocationFromCache(cacheKey)

        if (cachedData) {
          console.log('使用缓存的位置数据:', cachedData)
          this.currentLocation = cachedData
          this.updateLocationDisplay()
          return
        }

        // 缓存不存在或已过期，从API获取数据
        const response = await getCurrentLocation()
        console.log('location response', response)

        if (response.data && response.data.status) {
          const locationData = response.data.data
          this.currentLocation = {
            ip: locationData.ip,
            city: locationData.address.city,
            province: locationData.address.province,
            district: locationData.address.district,
            nation: locationData.address.nation,
            formattedAddress: locationData.formatted_address,
            lat: locationData.location.lat,
            lng: locationData.location.lng,
            adcode: locationData.address.adcode,
            nationCode: locationData.address.nation_code,
            requestId: locationData.request_id
          }

          // 将数据保存到缓存
          this.saveLocationToCache(cacheKey, this.currentLocation)
          console.log('当前位置:', this.currentLocation)

          this.updateLocationDisplay()
        } else {
          console.error('获取位置信息失败:', (response.data && response.data.msg) || '未知错误')
        }
      } catch (error) {
        console.error('获取位置信息出错:', error)
      } finally {
        this.locationLoading = false
      }
    },

    // 从缓存中获取位置数据
    getLocationFromCache (cacheKey) {
      try {
        const cachedItem = localStorage.getItem(cacheKey)
        if (!cachedItem) {
          return null
        }

        const parsedItem = JSON.parse(cachedItem)
        const now = new Date().getTime()
        const cacheTime = parsedItem.timestamp
        const cacheExpiry = 30 * 60 * 1000 // 30分钟，单位毫秒

        // 检查缓存是否过期
        if (now - cacheTime > cacheExpiry) {
          localStorage.removeItem(cacheKey)
          console.log('位置缓存已过期，已清除')
          return null
        }

        return parsedItem.data
      } catch (error) {
        console.error('读取位置缓存失败:', error)
        localStorage.removeItem(cacheKey)
        return null
      }
    },

    // 将位置数据保存到缓存
    saveLocationToCache (cacheKey, locationData) {
      try {
        const cacheItem = {
          data: locationData,
          timestamp: new Date().getTime()
        }
        localStorage.setItem(cacheKey, JSON.stringify(cacheItem))
        console.log('位置数据已缓存，有效期30分钟')
      } catch (error) {
        console.error('保存位置缓存失败:', error)
      }
    },

    // 更新位置显示
    updateLocationDisplay () {
      // 如果图表已经初始化，更新图表以显示定位图标
      if (this.chartInstance && !this.chartInstance.isDisposed()) {
        this.updateChartOption()
        // 启动定位图标的跳动动画
        setTimeout(() => {
          this.startLocationIconAnimation()
        }, 1000) // 延迟1秒启动动画，确保图表更新完成
      }
    },

    // 获取省份坐标的方法（包含主要省份）
    getCityCoordinates (provinceName) {
      const provinceCoords = {
        '北京市': [116.4074, 39.9042],
        '上海': [121.4737, 31.2304],
        '广东': [113.2644, 23.1291],
        '浙江': [120.1551, 30.2741],
        '江苏': [118.7969, 32.0603],
        '湖北': [114.3054, 30.5931],
        '四川': [104.0665, 30.5723],
        '重庆': [106.5516, 29.5630],
        '陕西': [108.9398, 34.3416],
        '天津': [117.2008, 39.0842],
        '河南': [113.6254, 34.7466],
        '湖南': [112.9388, 28.2282],
        '山东': [117.1205, 36.6519],
        '辽宁': [123.4315, 41.8057],
        '吉林': [125.3245, 43.8171],
        '黑龙江': [126.5358, 45.8023],
        '河北': [114.5149, 38.0428],
        '山西': [112.5489, 37.8706],
        '内蒙古': [111.7519, 40.8414],
        '江西': [115.8921, 28.6765],
        '安徽': [117.2272, 31.8206],
        '云南': [102.8329, 24.8801],
        '贵州': [106.6302, 26.6477],
        '西藏': [91.1409, 29.6456],
        '甘肃': [103.8236, 36.0581],
        '青海': [101.7782, 36.6171],
        '宁夏': [106.2309, 38.4872],
        '新疆': [87.6177, 43.7928],
        '海南': [110.3312, 20.0311],
        '福建': [119.2965, 26.0745],
        '广西': [108.3669, 22.8176],
        '台湾': [121.5654, 25.0330],
        '香港': [114.1694, 22.3193],
        '澳门': [113.5491, 22.1987]
      }

      // 尝试精确匹配
      if (provinceCoords[provinceName]) {
        return provinceCoords[provinceName]
      }

      // 尝试模糊匹配（去掉"省"、"市"、"自治区"等后缀）
      const provinceNameWithoutSuffix = provinceName.replace(/(省|市|自治区|特别行政区)$/, '')
      for (const [key, coords] of Object.entries(provinceCoords)) {
        if (key.includes(provinceNameWithoutSuffix) || provinceNameWithoutSuffix.includes(key)) {
          return coords
        }
      }

      return null // 未找到坐标
    },

    initChart () {
      this.$nextTick(() => {
        const chartContainer = this.$refs.cityChart
        if (!chartContainer) return

        // 销毁已有实例
        if (this.chartInstance) {
          this.chartInstance.dispose()
          this.chartInstance = null
        }

        // 初始化图表
        const chinaJson = require('../china.json')
        echarts.registerMap('china', chinaJson)
        this.chartInstance = echarts.init(chartContainer)

        // 科技感配置项
        const option = {
          backgroundColor: 'transparent',
          // 设置grid来减少边距
          grid: {
            left: '2%',
            right: '2%',
            top: '2%',
            bottom: '2%',
            containLabel: false
          },
          tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(0, 30, 80, 0.9)',
            borderColor: '#0096ff',
            borderWidth: 1,
            textStyle: {
              color: '#b8d4f0',
              fontSize: this.fontSizes.tooltipFontSize
            },
            formatter: (params) => {
              console.log('params---', params)
              // 如果是定位图标
              if (params.seriesName === '当前位置') {
                // 查找当前城市的设备数量
                const currentCityData = this.cityData.find(item => item.name === this.currentLocation.city)
                const deviceCount = currentCityData ? currentCityData.devices : 0

                return `
                                    <div style="padding: 10px;">
                                        <div style="color: #ff4757; font-weight: bold; margin-bottom: 8px; display: flex; align-items: center;">
                                            <span style="display: inline-block; width: 8px; height: 8px; background: #ff4757; border-radius: 50%; margin-right: 6px; animation: pulse 1.5s infinite;"></span>
                                            当前位置
                                        </div>
                                        <div style="color: #ffffff; margin-bottom: 4px;">IP地址: <span style="color: #0096ff;">${this.currentLocation.ip || '未知'}</span></div>
                                        <div style="color: #ffffff; margin-bottom: 4px;">位置: <span style="color: #0096ff;">${this.currentLocation.city || '未知'}</span></div>
                                        <div style="color: #ffffff; margin-bottom: 4px;">经纬度: <span style="color: #0096ff;">${this.currentLocation.lng || 0}, ${this.currentLocation.lat || 0}</span></div>
                                        <div style="color: #ffffff;">设备数量: <span style="color: #ff6b35;">${deviceCount}台</span></div>
                                    </div>
                                `
              }

              // 对于地图区域，直接使用params.name查找数据
              const data = this.cityData.find(item => item.name === params.name)
              console.log('data', data)
              if (data) {
                return `
                                    <div style="padding: 8px;">
                                        <div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params.name}</div>
                                        <div>设备数量: <span style="color: #ff6b35;">${data.devices}台</span></div>
                                    </div>
                                `
              }
              // 如果没有找到数据，只显示地区名称
              return `
                                    <div style="padding: 8px;">
                                        <div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params.name}</div>
                                        <div>设备数量: <span style="color: #ff6b35;">0台</span></div>
                                    </div>
                                `
            },
            extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
          },
          visualMap: {
            show: false, // 隐藏视觉映射组件，使用统一的地图配色
            min: 0,
            max: Math.max(...this.cityData.map(item => item.value), 300), // 动态计算最大值
            inRange: {
              color: ['#6FC7EA', '#6ba3d0', '#4a90c2', '#2c5aa0'] // 浅蓝色渐变
            }
          },
          geo: {
            map: 'china',
            roam: true,
            zoom: 1.7, // 增大缩放比例，减少空白区域
            center: [105, 35], // 微调中心点，优化展示位置
            aspectScale: 0.9, // 调整纵横比，让地图更紧凑
            // 设置地图的外边距
            layoutCenter: ['50%', '50%'],
            layoutSize: '95%', // 地图占满容器的95%
            label: {
              show: true, // 默认显示省份名称
              color: '#ffffff',
              fontSize: this.fontSizes.dataLabelFontSize - 5,
              fontWeight: 'bold'
            },
            itemStyle: {
              areaColor: '#4a90c2', // 统一的浅蓝色填充
              borderColor: '#2c5aa0', // 深蓝色边框
              borderWidth: 1
            },
            emphasis: {
              label: {
                show: true,
                color: '#0096ff',
                fontSize: this.fontSizes.dataLabelFontSize + 2,
                fontWeight: 'bold'
              },
              itemStyle: {
                areaColor: '#87ceeb', // 悬停时稍亮的浅蓝色
                borderColor: '#0096ff',
                borderWidth: 2
              }
            },
            regions: [
              {
                name: '南海诸岛',
                itemStyle: {
                  areaColor: '#4a90c2',
                  borderColor: '#2c5aa0',
                  opacity: 0.8
                }
              }
            ]
          },
          series: [
            {
              name: '设备分布',
              type: 'map',
              geoIndex: 0,
              data: this.cityData.map(item => ({ name: item.name, value: item.value })),
              label: {
                show: true, // 默认显示设备台数
                color: '#ffff00',
                fontSize: this.fontSizes.smallFontSize,
                fontWeight: 'bold',
                formatter: (params) => {
                  const data = this.cityData.find(item => item.name === params.name)
                  return data && data.devices > 0 ? `${data.devices}台` : ''
                }
              },
              emphasis: {
                label: {
                  show: true,
                  color: '#ff6b35',
                  fontSize: this.fontSizes.dataLabelFontSize,
                  fontWeight: 'bold',
                  formatter: (params) => {
                    const data = this.cityData.find(item => item.name === params.name)
                    return data && data.devices > 0 ? `${data.devices}台` : ''
                  }
                }
              }
            },
            // 当前位置定位图标
            {
              name: '当前位置',
              type: 'scatter',
              coordinateSystem: 'geo',
              data: this.currentLocation && this.currentLocation.lat && this.currentLocation.lng
                ? [{
                  name: this.currentLocation.city,
                  value: [this.currentLocation.lng, this.currentLocation.lat, this.currentLocation.city]
                }] : [],
              symbol: `image://${posIcon}`, // 使用pos.png定位图标
              symbolSize: [32, 32], // 图标大小
              itemStyle: {
                opacity: 1, // 图片透明度
                shadowColor: 'rgba(0, 0, 0, 0.3)',
                shadowBlur: 8,
                shadowOffsetX: 2,
                shadowOffsetY: 2
              },
              label: {
                show: true,
                position: 'bottom',
                color: '#ff4757',
                fontSize: this.fontSizes.dataLabelFontSize,
                fontWeight: 'bold',
                formatter: '',
                offset: [0, 5]
              },
              emphasis: {
                itemStyle: {
                  opacity: 0.8,
                  shadowBlur: 12,
                  shadowOffsetX: 3,
                  shadowOffsetY: 3
                },
                label: {
                  fontSize: this.fontSizes.dataLabelFontSize + 2
                }
              },
              zlevel: 2, // 确保定位图标在最上层
              // 跳动动画配置
              animationDuration: 1500,
              animationEasing: 'elasticOut',
              animationDelay: 500,
              animationDurationUpdate: 800,
              animationEasingUpdate: 'bounceOut'
            }
          ],
          animationDuration: 2000,
          animationEasing: 'cubicInOut'
        }

        // 设置配置项并渲染图表
        this.chartInstance.setOption(option)

        // 响应式调整
        this.resizeHandler = () => {
          if (this.chartInstance && !this.chartInstance.isDisposed()) {
            this.chartInstance.resize()
          }
        }

        window.addEventListener('resize', this.resizeHandler)
      })
    },

    updateChartOption () {
      if (!this.chartInstance || this.chartInstance.isDisposed()) {
        this.initChart()
        return
      }

      // 科技感配置项
      const option = {
        backgroundColor: 'transparent',
        // 设置grid来减少边距
        grid: {
          left: '2%',
          right: '2%',
          top: '2%',
          bottom: '2%',
          containLabel: false
        },
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(0, 30, 80, 0.9)',
          borderColor: '#0096ff',
          borderWidth: 1,
          textStyle: {
            color: '#b8d4f0',
            fontSize: this.fontSizes.tooltipFontSize
          },
          formatter: (params) => {
            // 如果是定位图标
            if (params.seriesName === '当前位置') {
              // 查找当前城市的设备数量
              const currentCityData = this.cityData.find(item => item.name === this.currentLocation.city)
              const deviceCount = currentCityData ? currentCityData.devices : 0

              return `
                                <div style="padding: 10px;">
                                    <div style="color: #ff4757; font-weight: bold; margin-bottom: 8px; display: flex; align-items: center;">
                                        <span style="display: inline-block; width: 8px; height: 8px; background: #ff4757; border-radius: 50%; margin-right: 6px; animation: pulse 1.5s infinite;"></span>
                                        当前位置
                                    </div>
                                    <div style="color: #ffffff; margin-bottom: 4px;">IP地址: <span style="color: #0096ff;">${this.currentLocation.ip || '未知'}</span></div>
                                    <div style="color: #ffffff; margin-bottom: 4px;">位置: <span style="color: #0096ff;">${this.currentLocation.city || '未知'}</span></div>
                                    <div style="color: #ffffff; margin-bottom: 4px;">经纬度: <span style="color: #0096ff;">${this.currentLocation.lng || 0}, ${this.currentLocation.lat || 0}</span></div>
                                    <div style="color: #ffffff;">设备数量: <span style="color: #ff6b35;">${deviceCount}台</span></div>
                                </div>
                            `
            }

            // 对于地图区域，直接使用params.name查找数据
            const data = this.cityData.find(item => item.name === params.name)
            if (data) {
              return `
                                <div style="padding: 8px;">
                                    <div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params.name}</div>
                                    <div>设备数量: <span style="color: #ff6b35;">${data.devices}台</span></div>
                                </div>
                            `
            }
            // 如果没有找到数据，只显示地区名称
            return `
                                <div style="padding: 8px;">
                                    <div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params.name}</div>
                                    <div>设备数量: <span style="color: #ff6b35;">0台</span></div>
                                </div>
                            `
          },
          extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
        },
        visualMap: {
          show: false, // 隐藏视觉映射组件，使用统一的地图配色
          min: 0,
          max: Math.max(...this.cityData.map(item => item.value), 300), // 动态计算最大值
          inRange: {
            color: ['#6FC7EA', '#6ba3d0', '#4a90c2', '#2c5aa0'] // 浅蓝色渐变
          }
        },
        geo: {
          map: 'china',
          roam: true,
          zoom: 1.7, // 增大缩放比例，减少空白区域
          center: [105, 35], // 微调中心点，优化展示位置
          aspectScale: 0.9, // 调整纵横比，让地图更紧凑
          // 设置地图的外边距
          layoutCenter: ['50%', '50%'],
          layoutSize: '95%', // 地图占满容器的95%
          label: {
            show: true, // 默认显示省份名称
            color: '#ffffff',
            fontSize: this.fontSizes.dataLabelFontSize - 9,
            fontWeight: 'bold'
          },
          itemStyle: {
            areaColor: '#4a90c2', // 统一的浅蓝色填充
            borderColor: '#2c5aa0', // 深蓝色边框
            borderWidth: 1
          },
          emphasis: {
            label: {
              show: true,
              color: '#0096ff',
              fontSize: this.fontSizes.dataLabelFontSize + 2,
              fontWeight: 'bold'
            },
            itemStyle: {
              areaColor: '#87ceeb', // 悬停时稍亮的浅蓝色
              borderColor: '#0096ff',
              borderWidth: 2
            }
          },
          regions: [
            {
              name: '南海诸岛',
              itemStyle: {
                areaColor: '#4a90c2',
                borderColor: '#2c5aa0',
                opacity: 0.8
              }
            }
          ]
        },
        series: [
          {
            name: '设备分布',
            type: 'map',
            geoIndex: 0,
            data: this.cityData.map(item => ({ name: item.name, value: item.value })),
            label: {
              show: true, // 默认显示设备台数
              color: '#ffff00',
              fontSize: this.fontSizes.smallFontSize,
              fontWeight: 'bold',
              formatter: (params) => {
                const data = this.cityData.find(item => item.name === params.name)
                return data && data.devices > 0 ? `${data.devices}台` : ''
              }
            },
            emphasis: {
              label: {
                show: true,
                color: '#ff6b35',
                fontSize: this.fontSizes.dataLabelFontSize,
                fontWeight: 'bold',
                formatter: (params) => {
                  const data = this.cityData.find(item => item.name === params.name)
                  return data && data.devices > 0 ? `${data.devices}台` : ''
                }
              }
            }
          },
          // 当前位置定位图标
          {
            name: '当前位置',
            type: 'scatter',
            coordinateSystem: 'geo',
            data: this.currentLocation && this.currentLocation.lat && this.currentLocation.lng
              ? [{
                name: this.currentLocation.city,
                value: [this.currentLocation.lng, this.currentLocation.lat, this.currentLocation.city]
              }] : [],
            symbol: `image://${posIcon}`, // 使用pos.png定位图标
            symbolSize: [32, 32], // 图标大小
            itemStyle: {
              opacity: 1, // 图片透明度
              shadowColor: 'rgba(0, 0, 0, 0.3)',
              shadowBlur: 8,
              shadowOffsetX: 2,
              shadowOffsetY: 2
            },
            label: {
              show: true,
              position: 'bottom',
              color: '#ff4757',
              fontSize: this.fontSizes.dataLabelFontSize,
              fontWeight: 'bold',
              formatter: '',
              offset: [0, 8]
            },
            emphasis: {
              itemStyle: {
                opacity: 0.8,
                shadowBlur: 12,
                shadowOffsetX: 3,
                shadowOffsetY: 3
              },
              label: {
                fontSize: this.fontSizes.dataLabelFontSize + 2
              }
            },
            zlevel: 2, // 确保定位图标在最上层
            // 跳动动画配置
            animationDuration: 1500,
            animationEasing: 'elasticOut',
            animationDelay: 500,
            animationDurationUpdate: 800,
            animationEasingUpdate: 'bounceOut'
          }
        ],
        animationDuration: 2000,
        animationEasing: 'cubicInOut'
      }

      this.chartInstance.setOption(option, true)
    },

    // 启动定位图标的持续跳动动画
    startLocationIconAnimation () {
      if (this.locationAnimationTimer) {
        clearInterval(this.locationAnimationTimer)
      }

      this.locationAnimationTimer = setInterval(() => {
        if (this.chartInstance && this.currentLocation && this.currentLocation.city) {
          // 获取当前图表配置
          const currentOption = this.chartInstance.getOption()

          // 找到定位图标系列（第三个系列）
          if (currentOption.series && currentOption.series[2]) {
            // 创建跳动效果：先放大
            const bounceOption = {
              series: [
                currentOption.series[0],
                currentOption.series[1],
                {
                  ...currentOption.series[2],
                  symbolSize: [36, 36], // 放大
                  animationDuration: 300,
                  animationEasing: 'elasticOut'
                }
              ]
            }

            this.chartInstance.setOption(bounceOption, false)

            // 恢复原始大小
            setTimeout(() => {
              if (this.chartInstance && !this.chartInstance.isDisposed()) {
                const restoreOption = {
                  series: [
                    currentOption.series[0],
                    currentOption.series[1],
                    {
                      ...currentOption.series[2],
                      symbolSize: [32, 32], // 恢复原始大小
                      animationDuration: 400,
                      animationEasing: 'bounceOut'
                    }
                  ]
                }
                this.chartInstance.setOption(restoreOption, false)
              }
            }, 300)
          }
        }
      }, 3000) // 每3秒执行一次跳动动画
    },

    // 停止定位图标动画
    stopLocationIconAnimation () {
      if (this.locationAnimationTimer) {
        clearInterval(this.locationAnimationTimer)
        this.locationAnimationTimer = null
      }
    },

    destroyChart () {
      // 停止定位图标动画
      this.stopLocationIconAnimation()

      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    }
  }
}
</script>

<style scoped>
.china-device-map {
    width: 100%;
    height: 100%;
}

.chart-container {
    width: 100%;
    height: 100%;
    min-height: 300px;
}

/* 定位图标动画效果 */
@keyframes pulse {
    0% {
        opacity: 1;
        transform: scale(1);
    }

    50% {
        opacity: 0.5;
        transform: scale(1.2);
    }

    100% {
        opacity: 1;
        transform: scale(1);
    }
}

/* 跳动动画效果 */
@keyframes bounce {

    0%,
    20%,
    50%,
    80%,
    100% {
        transform: translateY(0) scale(1);
    }

    10% {
        transform: translateY(-8px) scale(1.05);
    }

    40% {
        transform: translateY(-4px) scale(1.02);
    }

    60% {
        transform: translateY(-2px) scale(1.01);
    }
}

/* 组合动画：跳动+脉冲 */
@keyframes bounceAndPulse {
    0% {
        transform: translateY(0) scale(1);
        opacity: 1;
    }

    25% {
        transform: translateY(-6px) scale(1.1);
        opacity: 0.8;
    }

    50% {
        transform: translateY(0) scale(1.15);
        opacity: 0.6;
    }

    75% {
        transform: translateY(-3px) scale(1.05);
        opacity: 0.8;
    }

    100% {
        transform: translateY(0) scale(1);
        opacity: 1;
    }
}
</style>
